import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useForm } from "@mantine/form";

import {
  IReadKeyParams,
  IReadKeyResult,
  readKey,
  savePersistedSession,
  signIn,
  useSession,
} from "@prostocheck/data-web";
import { CERTIFICATE_AUTHORITIES, SimpleResult } from "@prostocheck/shared";

import { INITIAL_KEY_FORM_VALUES } from "../../login.constants";
import { IDetectProgress, LoginStepEnum } from "../../login.models";
import { createKeyFormValidation } from "../../login.utils";
import { ILogin } from "./use-login.models";

/**
 * The sign-in flow, away from the markup: two steps, one key, and the two
 * calls that can fail. Once step two succeeds, the key and its certificates
 * are also persisted to `localStorage` (`savePersistedSession`) so a reload
 * restores the session instead of asking again — see ai/docs/kep-signing.md.
 */
export const useLogin = (): ILogin => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { startSession } = useSession();

  const [step, setStep] = useState<LoginStepEnum>(LoginStepEnum.Key);
  const [readResult, setReadResult] = useState<IReadKeyResult | null>(null);
  const [errorResult, setErrorResult] = useState<SimpleResult<unknown> | null>(
    null,
  );
  const [isBusy, setIsBusy] = useState(false);
  const [detectProgress, setDetectProgress] = useState<IDetectProgress | null>(
    null,
  );

  // The key file rarely carries its certificates, so the issuer's CMP service
  // has to be picked from the list ДПС publishes. That list is baked into the
  // bundle at build time (`tools/vite/ca-registry.mjs`) — no runtime fetch.
  const authorities = CERTIFICATE_AUTHORITIES;

  const form = useForm<IReadKeyParams>({
    initialValues: INITIAL_KEY_FORM_VALUES,
    validate: createKeyFormValidation(t),
    // A previous failure describes values that no longer exist on screen.
    onValuesChange: () => setErrorResult(null),
  });

  const readKeyFile = async (values: IReadKeyParams) => {
    setIsBusy(true);
    setErrorResult(null);
    setDetectProgress(null);

    const result = await readKey({
      ...values,
      authorities,
      onDetectProgress: (checked, total) =>
        setDetectProgress({ checked, total }),
    });

    setIsBusy(false);
    setDetectProgress(null);

    if (!result.success || !result.data) {
      setErrorResult(result);

      return;
    }

    setReadResult(result.data);
    setStep(LoginStepEnum.SignIn);
  };

  const signInWithKey = async () => {
    if (!readResult) return;

    setIsBusy(true);
    setErrorResult(null);

    const result = await signIn(readResult);

    setIsBusy(false);

    if (!result.success || !result.data) {
      setErrorResult(result);

      return;
    }

    startSession(result.data);

    if (form.values.file) {
      await savePersistedSession({
        keyBox: result.data.keyBox,
        file: form.values.file,
        password: form.values.password,
        expiresAt: result.data.owner.validTo,
      });
    }

    navigate("/", { replace: true });
  };

  const restart = () => {
    setStep(LoginStepEnum.Key);
    setReadResult(null);
    setErrorResult(null);
  };

  return {
    step,
    form,
    authorities,
    owner: readResult?.owner ?? null,
    errorResult,
    isBusy,
    detectProgress,
    readKeyFile,
    signInWithKey,
    restart,
  };
};
